/// <reference types="vite/client" />
/* ──────────────────────────────────────────────────────────────
   Talent platform data layer — Supabase accounts, profiles,
   casting projects, and voice-sample storage.

   Auth model: passwordless email (6-digit code OR magic link).
   An account's application IS its talent profile (talent_profiles,
   one row per user, RLS: own-row only). Voice samples live in the
   private `voice-samples` bucket under <user_id>/.

   When Supabase env vars are missing (VITE_SUPABASE_URL /
   VITE_SUPABASE_ANON_KEY), the page falls back to the legacy
   email-only application via Web3Forms and hides account features.
   ────────────────────────────────────────────────────────────── */
import { useEffect, useState } from 'react';
import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js';

const WEB3FORMS_ACCESS_KEY = 'd3cbc6a2-6428-46cc-83a8-ee15a7f434ae';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, '') || '';
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) || '';

export const supabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

/** Singleton client — null when env isn't configured (fallback mode). */
export const supabase: SupabaseClient | null = supabaseConfigured
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : null;

/* ── Reachability ──
   A free-tier Supabase project is auto-paused after 7 idle days, and a
   paused project's hostname stops resolving entirely. When that happens
   every auth / data call fails, so the pages check once whether the
   backend answers and fall back to the email-only flow if it doesn't.
   Result is cached for the page lifetime. */
let reachablePromise: Promise<boolean> | null = null;

/** Why the last health check failed — surfaced in fallback emails so the team can see what happened. */
let platformFailReason = supabaseConfigured ? 'not checked yet' : 'Supabase not configured (env vars missing at build)';
export function getPlatformFailReason(): string { return platformFailReason; }

const HEALTH_ATTEMPTS = 3;          // total tries
const HEALTH_TIMEOUT_MS = 10000;    // per try — slow mobile + cold TLS to Mumbai can exceed 6s
const HEALTH_RETRY_DELAY_MS = 1500;

async function probeOnce(): Promise<{ ok: boolean; reason: string }> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HEALTH_TIMEOUT_MS);
  const started = Date.now();
  try {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/health`, {
      headers: { apikey: SUPABASE_ANON_KEY },
      signal: ctrl.signal,
      cache: 'no-store',
    });
    // Any answer below 500 means the host is alive (401/403 would still be "reachable");
    // a paused project either fails DNS or answers 5xx.
    if (res.status < 500) return { ok: true, reason: 'ok' };
    return { ok: false, reason: `health endpoint returned HTTP ${res.status}` };
  } catch (err) {
    const e = err as { name?: string; message?: string };
    if (e?.name === 'AbortError') return { ok: false, reason: `health check timed out after ${Math.round((Date.now() - started) / 1000)}s` };
    return { ok: false, reason: `network error (${e?.message || 'fetch failed'}) — host unreachable, blocked, or offline` };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * One cached probe per page load, with retries so a single slow or dropped
 * request on mobile doesn't demote a healthy backend to email-only mode.
 * Pass { fresh: true } to re-probe (used at submit time for diagnostics).
 */
export function checkPlatformReachable(opts: { fresh?: boolean } = {}): Promise<boolean> {
  if (!supabaseConfigured) return Promise.resolve(false);
  if (!reachablePromise || opts.fresh) {
    reachablePromise = (async () => {
      const reasons: string[] = [];
      for (let attempt = 1; attempt <= HEALTH_ATTEMPTS; attempt++) {
        const r = await probeOnce();
        if (r.ok) { platformFailReason = 'ok'; return true; }
        reasons.push(`try ${attempt}: ${r.reason}`);
        if (attempt < HEALTH_ATTEMPTS) await new Promise(res => setTimeout(res, HEALTH_RETRY_DELAY_MS));
      }
      const online = typeof navigator !== 'undefined' && 'onLine' in navigator ? (navigator.onLine ? 'browser online' : 'browser reports OFFLINE') : 'online state unknown';
      platformFailReason = `${reasons.join('; ')} (${online})`;
      return false;
    })();
  }
  return reachablePromise;
}

/** true = backend answering, false = unreachable (paused / offline / not configured), null = still checking. */
export function usePlatformReachable(): boolean | null {
  const [reachable, setReachable] = useState<boolean | null>(supabaseConfigured ? null : false);
  useEffect(() => {
    if (!supabaseConfigured) return;
    let cancelled = false;
    checkPlatformReachable().then(ok => { if (!cancelled) setReachable(ok); });
    return () => { cancelled = true; };
  }, []);
  return reachable;
}

/** True when an error came from the network layer (host unreachable) rather than from Supabase itself. */
export function isNetworkError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  if (!e) return false;
  return e.name === 'AuthRetryableFetchError' || e.name === 'TypeError' || /fetch|network|Load failed/i.test(e.message || '');
}

/* ── Types ── */
export interface TalentProfile {
  track: 'professional' | 'everyday';
  full_name: string;
  email: string;
  phone?: string | null;
  city?: string | null;
  languages: string[];
  voice_gender?: string | null;
  age_range?: string | null;
  experience_level?: string | null;
  categories?: string[] | null;
  portfolio_url?: string | null;
  comfort?: string | null;
  availability?: string | null;
  heard_from?: string | null;
  notes?: string | null;
  consent: boolean;
  sample_path?: string | null;
  sample_source?: string | null;
}

export interface SampleFile {
  blob: Blob;
  name: string;
  source: 'upload' | 'recorded';
}

export interface CastingTeaser {
  id: string;
  title: string;
  project_type: string;
  languages: string[];
  city: string;
  format: string | null;
  deadline: string | null;
}

export interface CastingProject extends CastingTeaser {
  description: string | null;
  pay_note: string | null;
  status?: string;
}

export interface MyApplication {
  id: string;
  created_at: string;
  status: 'submitted' | 'under_review' | 'selected' | 'not_selected';
  team_note: string | null;
  project: {
    id: string;
    title: string;
    project_type: string;
    languages: string[];
    city: string | null;
    format: string | null;
    status: string;
    deadline: string | null;
  } | null;
}

export interface TalentSample {
  id: string;
  language: string;
  sample_path: string;
  source: string | null;
  created_at: string;
}

export interface Announcement {
  id: string;
  created_at: string;
  title: string;
  body: string;
}

/* ── Auth ── */
export async function sendLoginCode(email: string): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: `${window.location.origin}/voice-talent/portal`,
    },
  });
  if (error) throw error;
}

export async function verifyLoginCode(email: string, token: string): Promise<Session> {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
  if (error) throw error;
  if (!data.session) throw new Error('No session returned');
  return data.session;
}

export async function getSession(): Promise<Session | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
}

/* ── Casting board ── */
export async function fetchCastingCount(): Promise<number | null> {
  if (!supabase) return null;
  const { count, error } = await supabase.from('casting_teasers').select('*', { count: 'exact', head: true });
  if (error) return null;
  return count ?? 0;
}

export async function fetchTeasers(): Promise<CastingTeaser[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('casting_teasers')
    .select('id, title, project_type, languages, city, format, deadline')
    .order('created_at', { ascending: false })
    .limit(6);
  if (error) return [];
  return (data as CastingTeaser[]) ?? [];
}

export async function fetchProjects(): Promise<CastingProject[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('projects')
    .select('id, title, description, project_type, languages, city, format, pay_note, deadline')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as any[]).map(p => ({ ...p, city: p.city || 'Remote' })) as CastingProject[];
}

export async function fetchMyApplications(): Promise<string[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('project_applications').select('project_id');
  if (error) return [];
  return (data ?? []).map((r: any) => r.project_id as string);
}

/** Full application history (incl. projects that have since closed). */
export async function fetchMyApplicationsDetailed(): Promise<MyApplication[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('project_applications')
    .select('id, created_at, status, team_note, projects(id, title, project_type, languages, city, format, status, deadline)')
    .order('created_at', { ascending: false });
  if (error) return [];
  return ((data ?? []) as any[]).map(r => ({
    id: r.id,
    created_at: r.created_at,
    status: r.status || 'submitted',
    team_note: r.team_note ?? null,
    project: r.projects ?? null,
  }));
}

/* ── Voice portfolio (per-language samples) ── */
export async function fetchMySamples(): Promise<TalentSample[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('talent_samples')
    .select('id, language, sample_path, source, created_at')
    .order('created_at', { ascending: true });
  if (error) return [];
  return (data ?? []) as TalentSample[];
}

export async function savePortfolioSample(userId: string, language: string, sample: SampleFile): Promise<TalentSample> {
  if (!supabase) throw new Error('Supabase not configured');
  const ext = sample.name.includes('.') ? sample.name.split('.').pop()! : (baseMime(sample.blob.type).split('/')[1] || 'bin');
  const langSlug = language.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const path = `${userId}/portfolio-${langSlug}-${Date.now()}.${ext.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
  const { error: upErr } = await supabase.storage.from('voice-samples').upload(path, sample.blob, {
    contentType: baseMime(sample.blob.type),
    upsert: true,
  });
  if (upErr) throw upErr;
  const { data, error } = await supabase
    .from('talent_samples')
    .upsert({ user_id: userId, language, sample_path: path, source: sample.source }, { onConflict: 'user_id,language' })
    .select('id, language, sample_path, source, created_at')
    .single();
  if (error) throw error;
  return data as TalentSample;
}

export async function deletePortfolioSample(sample: TalentSample): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');
  await supabase.storage.from('voice-samples').remove([sample.sample_path]).catch(() => {});
  const { error } = await supabase.from('talent_samples').delete().eq('id', sample.id);
  if (error) throw error;
}

/* ── Announcements ── */
export async function fetchAnnouncements(limit = 4): Promise<Announcement[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('announcements')
    .select('id, created_at, title, body')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as Announcement[];
}

export async function applyToProject(project: Pick<CastingProject, 'id' | 'title' | 'project_type' | 'languages' | 'city'>, userId: string, userEmail: string): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.from('project_applications').insert({ project_id: project.id, user_id: userId });
  // 23505 = already applied — treat as success (idempotent tap)
  if (error && error.code !== '23505') throw error;
  if (!error) {
    // Analytics: distinct event for project applications (profile-save keeps the ads conversion)
    (window as any).gtag?.('event', 'talent_project_apply', { project_type: project.project_type, project_title: project.title });
    // Heads-up to the team inbox — best-effort, the application row is already stored.
    void fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Casting Application — ${project.title}`,
        from_name: 'Voqals Talent Portal',
        replyto: userEmail,
        Project: project.title,
        Type: project.project_type,
        Languages: project.languages.join(', '),
        City: project.city || 'Remote',
        'Applicant email': userEmail,
        'Applicant user_id': userId,
        Note: 'Full profile is in Supabase → talent_profiles (match by email or user_id).',
      }),
    }).catch(() => {});
  }
}

/* ── Profile ── */
export async function fetchMyProfile(): Promise<(TalentProfile & { status: string; user_id: string }) | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.from('talent_profiles').select('*').maybeSingle();
  if (error) return null;
  return data as any;
}

const baseMime = (t: string) => (t || 'application/octet-stream').split(';')[0].trim();

async function uploadSample(userId: string, sample: SampleFile): Promise<string> {
  if (!supabase) throw new Error('Supabase not configured');
  const ext = sample.name.includes('.') ? sample.name.split('.').pop()! : (baseMime(sample.blob.type).split('/')[1] || 'bin');
  const path = `${userId}/sample-${Date.now()}.${ext.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
  const { error } = await supabase.storage.from('voice-samples').upload(path, sample.blob, {
    contentType: baseMime(sample.blob.type),
    upsert: true,
  });
  if (error) throw error;
  return path;
}

/** Signed URL for the talent's own sample (private bucket). */
export async function getSampleUrl(path: string): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.storage.from('voice-samples').createSignedUrl(path, 3600);
  if (error) return null;
  return data.signedUrl;
}

/** Create or update the caller's talent profile (+ optional new sample). */
export async function saveProfile(userId: string, profile: TalentProfile, sample: SampleFile | null): Promise<void> {
  if (!supabase) throw new Error('Supabase not configured');
  let sample_path = profile.sample_path ?? null;
  let sample_source = profile.sample_source ?? null;
  if (sample) {
    sample_path = await uploadSample(userId, sample);
    sample_source = sample.source;
  }
  const row = { ...profile, user_id: userId, sample_path, sample_source };
  const { error } = await supabase.from('talent_profiles').upsert(row, { onConflict: 'user_id' });
  if (error) throw error;
  // Heads-up email to the team — best-effort, profile is already stored.
  void notifyByEmail(profile, `Supabase profile (${userId})`, null);
}

/* ── Web3Forms — team notification + legacy no-Supabase fallback ── */
async function notifyByEmail(app: TalentProfile, storageNote: string, attachmentKey: string | null): Promise<boolean> {
  const payload: Record<string, string> = {
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: `Voice Talent Application — ${app.full_name} (${app.track})`,
    from_name: 'Voqals Voice Talent Page',
    replyto: app.email,
    Track: app.track === 'professional' ? 'Professional Voice' : 'Everyday Speaker',
    Name: app.full_name,
    Email: app.email,
    Phone: app.phone || '-',
    City: app.city || '-',
    Languages: app.languages.join(', '),
    'Voice / Gender': app.voice_gender || '-',
    'Age Range': app.age_range || '-',
    ...(app.track === 'professional'
      ? { Experience: app.experience_level || '-', Categories: (app.categories || []).join(', ') || '-', Portfolio: app.portfolio_url || '-' }
      : { 'Comfortable with': app.comfort || '-' }),
    Availability: app.availability || '-',
    'Heard from': app.heard_from || '-',
    Notes: app.notes || '-',
    Storage: storageNote,
  };
  if (attachmentKey) payload.attachment = attachmentKey;
  try {
    const res = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(r => r.json());
    return Boolean(res?.success);
  } catch {
    return false;
  }
}

async function uploadSampleToWeb3Forms(sample: SampleFile): Promise<string | null> {
  try {
    const presigned = await fetch(`https://api.web3forms.com/upload?file=${encodeURIComponent(sample.name)}`).then(r => r.json());
    if (!presigned?.url) return null;
    const formData = new FormData();
    if (presigned.fields) Object.entries(presigned.fields as Record<string, string>).forEach(([k, v]) => formData.append(k, v));
    formData.append('Content-Type', baseMime(sample.blob.type));
    formData.append('file', new File([sample.blob], sample.name, { type: baseMime(sample.blob.type) }));
    const res = await fetch(presigned.url, { method: 'POST', body: formData });
    return res.ok ? (presigned.key as string) : null;
  } catch {
    return null;
  }
}

/** Legacy path when Supabase isn't configured: email-only application. */
/**
 * Email-only application path. `why` explains which condition sent the
 * applicant here (it lands in the "Storage" line of the team email), e.g.
 * "backend unreachable at page load: try 1: timed out…" or "no session".
 */
export async function submitFallbackApplication(app: TalentProfile, sample: SampleFile | null, why?: string): Promise<void> {
  const attachmentKey = sample ? await uploadSampleToWeb3Forms(sample) : null;
  const reason = why || (supabaseConfigured ? getPlatformFailReason() : 'Supabase not configured');
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : 'unknown UA';
  const ok = await notifyByEmail(app, `Email only — ${reason} — ${ua}`, attachmentKey);
  if (!ok) throw new Error('Submission failed — email channel rejected the application.');
}
